import { type AmplifyError, signIn } from '@backoffice-frontend/authentication'
import { useMediaQuery, useTheme } from '@backoffice-frontend/patterns'
import { Callout } from '@moia-dev/pace-web'
import type { ConfirmSignInOutput } from 'aws-amplify/auth'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PublicAppViewContentLayout } from '../components/PublicAppViewContentLayout'
import { PasswordChallengeForm } from '../PasswordChallengeForm'
import { LoginForm } from './LoginForm'
import { MultiFactorAuthenticationForm } from './MultiFactorAuthenticationForm'

const loginSteps = Object.freeze({
  CREDENTIALS: 'CREDENTIALS',
  MULTI_FACTOR_AUTHENTICATION: 'MULTI_FACTOR_AUTHENTICATION',
  PASSWORD_RESET: 'PASSWORD_RESET',
})

type CognitoLoginViewProps = {
  onClose: VoidFunction
  title: string
  description?: string
}

export const CognitoLoginView = ({
  onClose,
  title,
  description,
}: CognitoLoginViewProps) => {
  const [loginStep, setLoginStep] = useState<string>(loginSteps.CREDENTIALS)
  const [validationError, setValidationError] = useState('')

  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleNextSignInStep = (nextStep: ConfirmSignInOutput['nextStep']) => {
    if (nextStep.signInStep === 'DONE') {
      return
    } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      setLoginStep(loginSteps.MULTI_FACTOR_AUTHENTICATION)
    } else if (
      nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      setLoginStep(loginSteps.PASSWORD_RESET)
    } else {
      console.error('Unknown next sign in step:', nextStep.signInStep)
    }
  }

  const handleCredentialsSubmit = async (credentials: {
    username: string
    password: string
  }) => {
    setValidationError('')
    try {
      const { nextStep } = await signIn({
        username: credentials.username.trim(),
        password: credentials.password,
      })

      handleNextSignInStep(nextStep)
    } catch (_error) {
      const error = _error as AmplifyError
      if (error.name === 'NotAuthorizedException') {
        setValidationError(t('loginView_invalidCredentialsError'))
      } else {
        setValidationError(error.message)
      }
    }
  }

  const getTitle = () => {
    switch (loginStep) {
      case loginSteps.MULTI_FACTOR_AUTHENTICATION:
        return t('loginView_multiFactorAuthentication_header')

      case loginSteps.CREDENTIALS:
      default:
        return title
    }
  }

  return (
    <PublicAppViewContentLayout
      title={getTitle()}
      description={description}
      isMobile={isMobile}
    >
      {validationError && (
        <div
          css={theme => ({
            marginTop: isMobile ? theme.space.Space2 : theme.space.Space6,
          })}
        >
          <Callout variant="critical" title={t('loginView_generalError')}>
            {validationError}
          </Callout>
        </div>
      )}
      {loginStep === loginSteps.CREDENTIALS && (
        <LoginForm
          onSubmit={handleCredentialsSubmit}
          isMobile={isMobile}
          onClose={onClose}
        />
      )}
      {loginStep === loginSteps.MULTI_FACTOR_AUTHENTICATION && (
        <MultiFactorAuthenticationForm isMobile={isMobile} />
      )}
      {loginStep === loginSteps.PASSWORD_RESET && (
        <PasswordChallengeForm
          isMobile={isMobile}
          onSuccess={({ nextStep }) => {
            handleNextSignInStep(nextStep)
          }}
          onClose={() => setLoginStep(loginSteps.CREDENTIALS)}
        />
      )}
    </PublicAppViewContentLayout>
  )
}
